import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
import { Package, X, Search, Link2, Trash2, Download, Upload, Pencil } from 'lucide-react';
import { listSurveysFromCloud, listPackageEmbedders } from '../../utils/cloudApi';

/**
 * PackagePanel (WP-6 picker + WP-7 management)
 *
 * One panel, two modes over the same kind=package list:
 *  - 'picker'      (Add Node → Package): select-to-bind. onPick(row) binds.
 *  - 'management'  (Packages button): links dropdown, edit, delete, import/export.
 *
 * Placeholder styling — WP-8 reconciles visuals with the design tokens.
 */

const parseSettings = (row) => {
  const s = row?.settings;
  if (!s) return {};
  if (typeof s === 'string') { try { return JSON.parse(s); } catch { return {}; } }
  return s;
};

const rowTags = (row) => {
  if (Array.isArray(row?.tags)) return row.tags;
  const s = parseSettings(row);
  return Array.isArray(s.tags) ? s.tags : [];
};

export default function PackagePanel({
  open,
  mode = 'picker',
  onClose,
  onPick,            // picker: (row) => void
  onOpenForEdit,     // management: (row) => void
  onDelete,          // management: async (row, embedders) => void
  onEditMeta,        // management: (row) => void
  onExport,          // management: (row) => void
  onImport,          // management: () => void
  reloadSignal = 0,  // bump to force a list refetch after an external mutation
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [linksFor, setLinksFor] = useState(null);     // {id, name, embedders|null}

  const fetchList = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { items } = await listSurveysFromCloud({ kind: 'package', limit: 200, offset: 0, search: '' });
      setRows(items || []);
    } catch (e) {
      setError(e.message || 'Failed to load packages');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setLinksFor(null); fetchList(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchList, reloadSignal]);

  const allTags = useMemo(() => {
    const t = new Set();
    rows.forEach((r) => rowTags(r).forEach((x) => t.add(x)));
    return [...t].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const name = (r.title || r.name || '').toLowerCase();
      const desc = (r.description || '').toLowerCase();
      const matchesText = !q || name.includes(q) || desc.includes(q);
      const matchesTag = !activeTag || rowTags(r).includes(activeTag);
      return matchesText && matchesTag;
    });
  }, [rows, search, activeTag]);

  const selectedRow = filtered.find((r) => r.id === selectedId);

  const loadLinks = async (row) => {
    if (linksFor && linksFor.id === row.id) { setLinksFor(null); return; }
    setLinksFor({ id: row.id, name: row.title || row.name, embedders: null });
    try {
      const embedders = await listPackageEmbedders(row.id);
      setLinksFor({ id: row.id, name: row.title || row.name, embedders });
    } catch (e) {
      setLinksFor({ id: row.id, name: row.title || row.name, embedders: [], error: e.message });
    }
  };

  const title = mode === 'picker' ? 'Insert a Package' : 'Packages';

  return (
    <Dialog open={open} onClose={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)' }} aria-hidden="true" />
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Dialog.Panel style={{ background: '#fff', borderRadius: 8, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', width: 'min(760px, 96vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: '1px solid #E8EAED' }}>
            <Package size={18} color="#3D85E6" />
            <Dialog.Title style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>{title}</Dialog.Title>
            {mode === 'management' && (
              <button onClick={onImport} title="Import a .sampkg file" style={btnSm}>
                <Upload size={14} /> Import
              </button>
            )}
            <button onClick={onClose} title="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={18} /></button>
          </div>

          <div style={{ display: 'flex', gap: 8, padding: '10px 18px', borderBottom: '1px solid #F0F2F4', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: '#889' }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search packages…"
                style={{ width: '100%', padding: '7px 8px 7px 28px', border: '1px solid #CBD2D9', borderRadius: 4, fontSize: 13 }}
              />
            </div>
          </div>

          {allTags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 18px', borderBottom: '1px solid #F0F2F4' }}>
              <button onClick={() => setActiveTag('')} style={tagChip(activeTag === '')}>All</button>
              {allTags.map((t) => (
                <button key={t} onClick={() => setActiveTag(t === activeTag ? '' : t)} style={tagChip(activeTag === t)}>{t}</button>
              ))}
            </div>
          )}

          <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
            {loading && <div style={{ padding: 20, color: '#667', textAlign: 'center' }}>Loading…</div>}
            {error && <div style={{ padding: 12, color: '#a94442', background: '#f2dede', borderRadius: 4 }}>{error}</div>}
            {!loading && !error && filtered.length === 0 && (
              <div style={{ padding: 24, color: '#889', textAlign: 'center' }}>No packages{search || activeTag ? ' match your filter' : ' yet'}.</div>
            )}
            {filtered.map((row) => {
              const s = parseSettings(row);
              const tags = rowTags(row);
              const qc = s.questionCount;
              const isSel = selectedId === row.id;
              return (
                <div
                  key={row.id}
                  onClick={() => setSelectedId(row.id)}
                  onDoubleClick={() => mode === 'picker' && onPick?.(row)}
                  style={{
                    border: `1px solid ${isSel ? '#5B9FFF' : '#E8EAED'}`,
                    background: isSel ? '#F5F9FF' : '#fff',
                    borderRadius: 6, padding: '10px 12px', marginBottom: 8, cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Package size={15} color="#3D85E6" />
                    <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{row.title || row.name || '(untitled)'}</span>
                    <span style={{ fontSize: 11, color: '#889' }}>v{row.version || 1}</span>
                  </div>
                  {row.description && <div style={{ fontSize: 12, color: '#556', margin: '4px 0' }}>{row.description}</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 4 }}>
                    {tags.map((t) => <span key={t} style={{ background: '#EBF4FF', color: '#2B6BBF', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{t}</span>)}
                    {typeof qc === 'number' && <span style={{ fontSize: 11, color: '#778' }}>{qc} question{qc === 1 ? '' : 's'}</span>}
                    {row.updated_at && <span style={{ fontSize: 11, color: '#99a', marginLeft: 'auto' }}>{new Date(row.updated_at).toLocaleDateString()}</span>}
                  </div>

                  {mode === 'management' && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      <button onClick={(e) => { e.stopPropagation(); onOpenForEdit?.(row); }} style={btnSm}><Pencil size={12} /> Open</button>
                      <button onClick={(e) => { e.stopPropagation(); onEditMeta?.(row); }} style={btnSm}>Edit tags</button>
                      <button onClick={(e) => { e.stopPropagation(); loadLinks(row); }} style={btnSm}><Link2 size={12} /> Links</button>
                      <button onClick={(e) => { e.stopPropagation(); onExport?.(row); }} style={btnSm}><Download size={12} /> Export</button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(row); }} style={{ ...btnSm, color: '#d9534f', borderColor: '#e6b3b3' }}><Trash2 size={12} /> Delete</button>
                    </div>
                  )}

                  {mode === 'management' && linksFor && linksFor.id === row.id && (
                    <div style={{ marginTop: 8, padding: 8, background: 'var(--color-bg-base)', borderRadius: 4, fontSize: 12 }}>
                      {linksFor.embedders === null && <span style={{ color: '#889' }}>Loading links…</span>}
                      {linksFor.embedders && linksFor.embedders.length === 0 && <span style={{ color: '#889' }}>Not embedded in any current survey version.</span>}
                      {linksFor.embedders && linksFor.embedders.length > 0 && (
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Embedded in:</div>
                          {linksFor.embedders.map((emb) => (
                            <div key={emb.survey_id}>• {emb.name || emb.survey_id} <span style={{ color: '#99a' }}>v{emb.version}</span></div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {mode === 'picker' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '1px solid #E8EAED' }}>
              <button onClick={onClose} style={{ padding: '8px 14px', border: '1px solid #CBD2D9', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={() => selectedRow && onPick?.(selectedRow)}
                disabled={!selectedRow}
                style={{ padding: '8px 14px', border: '1px solid #3D85E6', background: selectedRow ? '#5B9FFF' : '#A9C9F5', color: '#fff', borderRadius: 4, cursor: selectedRow ? 'pointer' : 'not-allowed' }}
              >
                Insert package
              </button>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );

  // Delete: fetch embedders for the informed-delete confirmation (WP-7).
  async function handleDelete(row) {
    let embedders = [];
    try { embedders = await listPackageEmbedders(row.id); } catch (_) { /* proceed */ }
    onDelete?.(row, embedders);
  }
}

const btnSm = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px',
  border: '1px solid #CBD2D9', background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 12,
};
const tagChip = (active) => ({
  padding: '3px 10px', borderRadius: 12, fontSize: 12, cursor: 'pointer',
  border: `1px solid ${active ? '#3D85E6' : '#CBD2D9'}`,
  background: active ? '#5B9FFF' : '#fff', color: active ? '#fff' : '#445',
});
